import { api } from './api'
import { quotesApi } from './quotes'

const apiVersion = 'v2'

const quotePath = ({ organisationId, quoteId }) => `/${apiVersion}/organisations/${organisationId}/quotes/${quoteId}`

export const quoteAttachmentsPath = (args) => `${quotePath(args)}/attachments`

const attachmentsTag = (_result, _error, { quoteId }) => [{ type: 'QuoteAttachments', id: quoteId }]

// The quote screen's getQuote response already carries the rows, so copy them into the panel's
// entry. Opening a quote then costs one request, not two in a row.
quotesApi.enhanceEndpoints({
    endpoints: {
        getQuote: {
            async onQueryStarted({ organisationId, quoteId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    // upsertQueryEntries is synchronous, unlike upsertQueryData, so the entry is
                    // already fulfilled when the panel mounts and subscribes.
                    dispatch(
                        api.util.upsertQueryEntries([
                            {
                                endpointName: 'getQuoteAttachments',
                                arg: { organisationId, quoteId },
                                value: data?.attachments ?? [],
                            },
                        ])
                    )
                } catch {
                    // A failed quote request is handled by the quote screen; the panel fetches on its own.
                }
            },
        },
    },
})

export const quoteAttachmentsApi = api
    .enhanceEndpoints({
        addTagTypes: ['QuoteAttachments'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            // There is no attachments list endpoint; the rows ride on the quote payload. This reads
            // the same URL as getQuote but keeps its own cache entry, so a documents write refreshes
            // the panel without refreshing (and re-rendering) the rest of the quote screen.
            getQuoteAttachments: builder.query({
                query: quotePath,
                transformResponse: (quote) => quote?.attachments ?? [],
                providesTags: attachmentsTag,
            }),

            setQuoteAttachmentVisibility: builder.mutation({
                query: ({ documentId, isPublic, organisationId, quoteId }) => ({
                    url: `${quoteAttachmentsPath({ organisationId, quoteId })}/${documentId}/visibility`,
                    method: 'PATCH',
                    body: { isPublic },
                }),
                invalidatesTags: attachmentsTag,
            }),

            deleteQuoteAttachment: builder.mutation({
                query: ({ documentId, organisationId, quoteId }) => ({
                    url: `${quoteAttachmentsPath({ organisationId, quoteId })}/${documentId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: attachmentsTag,
            }),
        }),
    })

export const {
    useDeleteQuoteAttachmentMutation,
    useGetQuoteAttachmentsQuery,
    useSetQuoteAttachmentVisibilityMutation,
} = quoteAttachmentsApi
